/**
 * Aba Integracoes: webhooks das plataformas (tres dedicados + o generico),
 * WhatsApp (Evolution), voz (Vapi) e e-mail (a definir).
 *
 * Decisoes nao obvias:
 *  - WhatsApp fica FORA do rascunho e o estado dele mora na PAGINA (`whatsapp`
 *    + `onWhatsAppChange`). "Reconectar" e uma acao com efeito imediato (na
 *    versao real, a Evolution troca o estado da instancia), nao um campo que
 *    se salva; e a prontidao no topo da pagina precisa refletir a reconexao
 *    na hora, sem esperar "Salvar".
 *  - "Salvar na previa" devolve a fatia salva para a pagina (`onSaved`): e o
 *    que faz o checklist de prontidao ler a configuracao EFETIVA, e nao o que
 *    esta sendo digitado.
 *  - Plataformas conectadas vem antes na grade; as demais ficam tracejadas.
 *    A ordem e por estado, nao por nome, porque a pergunta de quem abre a aba
 *    e "o que esta funcionando?". O GENERICO tem secao propria, de largura
 *    inteira: ele carrega o contrato, e e por ele que qualquer plataforma sem
 *    adaptador entra hoje.
 *  - Os ids dos campos sao ESTAVEIS (`htmlFor`) para a lista de erros da
 *    barra conseguir levar o foco ate eles.
 *  - O e-mail mostra provedor "a definir" num Select desabilitado, e nao
 *    escondido: a pessoa ve que a escolha existe e esta pendente do dono.
 */

import { useState } from 'react';
import { Badge, Button, Field, Footnote, Icon, Input, Modal, Panel, SectionCard, Select } from '../../../components/ui';
import { formatCount, formatDateTime, maskPhoneDisplay } from '../../../lib/format';
import type { CompanySummary } from '../../../types';
import type { IntegrationsSettings, VoiceName, WhatsAppIntegration } from '../types';
import { useRascunho } from '../useRascunho';
import { VOICES } from '../vocabulario';
import { BarraSalvar, type ErroDeCampo } from './BarraSalvar';
import { CartaoGenerico } from './CartaoGenerico';
import { CartaoProvedor } from './CartaoProvedor';
import { QrCodeFalso } from './QrCodeFalso';

const DOMINIO_RE = /^[^\s/@]+\.[^\s/@]+$/;

const ID_ASSISTENTE = 'cfg-voz-assistente';
const ID_VOZ = 'cfg-voz-timbre';
const ID_DOMINIO = 'cfg-email-dominio';

function ehVoz(valor: string): valor is VoiceName {
  return VOICES.some((v) => v.value === valor);
}

function Dado({ rotulo, valor, mono = false, title }: { readonly rotulo: string; readonly valor: string; readonly mono?: boolean; readonly title?: string }): JSX.Element {
  return (
    <div className="min-w-0">
      <dt className="text-[10px] font-semibold uppercase tracking-wide text-ink-subtle" title={title}>
        {rotulo}
      </dt>
      <dd className={`mt-1 truncate text-sm text-ink ${mono ? 'font-mono text-xs' : ''}`}>{valor}</dd>
    </div>
  );
}

export function AbaIntegracoes({
  base,
  company,
  whatsapp,
  onWhatsAppChange,
  onSaved,
}: {
  readonly base: IntegrationsSettings;
  readonly company: CompanySummary;
  readonly whatsapp: WhatsAppIntegration;
  readonly onWhatsAppChange: (proximo: WhatsAppIntegration) => void;
  readonly onSaved: (salvo: IntegrationsSettings) => void;
}): JSX.Element {
  const rascunho = useRascunho(base);
  const [qrAberto, setQrAberto] = useState(false);
  const [pareado, setPareado] = useState(false);
  const [tentouSalvar, setTentouSalvar] = useState(false);

  const { draft } = rascunho;
  const erroAssistente = draft.voice.assistantName.trim() === '' ? 'Dê um nome ao assistente.' : null;
  const dominio = draft.email.sendingDomain.trim();
  const erroDominio = dominio !== '' && !DOMINIO_RE.test(dominio) ? 'Informe só o domínio, como envio.suaempresa.com.br.' : null;
  const erros: readonly ErroDeCampo[] = [
    erroAssistente === null ? null : { id: ID_ASSISTENTE, campo: 'Nome do assistente', mensagem: erroAssistente },
    erroDominio === null ? null : { id: ID_DOMINIO, campo: 'Domínio de envio', mensagem: erroDominio },
  ].filter((e): e is ErroDeCampo => e !== null);
  const temErro = erros.length > 0;

  const dedicados = base.webhooks.filter((w) => w.provider !== 'generic');
  const generico = base.webhooks.find((w) => w.provider === 'generic') ?? null;
  const conectados = base.webhooks.filter((w) => w.connected);
  const semSegredo = conectados.filter((w) => w.secretState === 'missing').length;
  const rejeitados7d = conectados.reduce((acc, w) => acc + w.rejectedLast7Days, 0);
  const ordenados = [...dedicados].sort((a, b) => Number(b.connected) - Number(a.connected));

  const fecharQr = (): void => setQrAberto(false);
  const simularLeitura = (): void => {
    onWhatsAppChange({ ...whatsapp, state: 'connected', connectedSince: new Date().toISOString() });
    setPareado(true);
    setQrAberto(false);
  };

  const salvar = (): void => {
    setTentouSalvar(true);
    if (!temErro) {
      rascunho.salvar();
      onSaved(draft);
      setTentouSalvar(false);
    }
  };

  const descartar = (): void => {
    setTentouSalvar(false);
    rascunho.descartar();
  };

  const desconectado = whatsapp.state === 'disconnected';

  return (
    <div className="flex flex-col gap-6">
      <SectionCard
        id="webhooks"
        title="Plataformas de checkout"
        icon="link"
        description="Cada plataforma avisa o motor sobre carrinho abandonado, pagamento aprovado, reembolso, chargeback e assinatura vencida nesta URL. O segredo prova que o evento veio dela."
        aside={
          <>
            <Badge tone={conectados.length > 0 ? 'good' : 'neutral'} dot>
              {formatCount(conectados.length)} de {formatCount(base.webhooks.length)} conectadas
            </Badge>
            {semSegredo > 0 ? (
              <Badge tone="warning" icon="chave">
                {formatCount(semSegredo)} sem segredo
              </Badge>
            ) : null}
            {rejeitados7d > 0 ? (
              <Badge tone="critical" icon="alerta">
                {formatCount(rejeitados7d)} rejeitados em 7 dias
              </Badge>
            ) : null}
          </>
        }
      >
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {ordenados.map((integracao) => (
            <CartaoProvedor key={integracao.provider} integracao={integracao} />
          ))}
        </div>
        <div className="mt-4">
          <Footnote>
            Um mesmo lead pode chegar por mais de uma plataforma; o motor casa pelo telefone. Reembolso e chargeback também chegam por aqui — é o que permite aplicar a política
            de estorno da empresa a uma comissão já creditada.
          </Footnote>
        </div>
      </SectionCard>

      {generico === null ? null : (
        <SectionCard
          id="webhook-generico"
          title="Checkout próprio e outras plataformas"
          icon="raio"
          description="Webhook genérico: o contrato canônico do Converte Fácil. Qualquer sistema que faça um POST com JSON entra no mesmo motor que Kiwify, Hotmart e Shopify."
          aside={
            <Badge tone="info" icon="modelo">
              Contrato canônico · pronto no motor
            </Badge>
          }
        >
          <CartaoGenerico integracao={generico} company={company} />
        </SectionCard>
      )}

      <SectionCard
        id="whatsapp"
        title="WhatsApp (Evolution API)"
        icon="whatsapp"
        description="Número que envia as mensagens da régua. Precisa de uma sessão ativa no aparelho da empresa."
        aside={
          <Badge tone={desconectado ? 'critical' : 'good'} dot>
            {desconectado ? 'Desconectado' : 'Conectado'}
          </Badge>
        }
      >
        <dl className="grid gap-4 sm:grid-cols-3">
          <Dado rotulo="Instância" valor={whatsapp.instanceName} mono title="Nome da sessão na Evolution API. Um por empresa." />
          <Dado rotulo="Número" valor={maskPhoneDisplay(whatsapp.phoneE164)} title="O número que o lead vê ao receber a mensagem." />
          <Dado rotulo="Conectado desde" valor={whatsapp.connectedSince === null ? 'Nunca conectou' : formatDateTime(whatsapp.connectedSince)} />
        </dl>
        {desconectado ? (
          <p className="mt-4 flex items-start gap-2 rounded-lg border border-status-critical/40 bg-status-critical/5 px-3 py-2 text-xs text-ink-muted">
            <Icon name="alerta" size="xs" className="mt-0.5 shrink-0 text-status-critical" />
            <span>Com a instância desconectada, o 2º tier (WhatsApp) não sai e a régua para depois das ligações. Reconecte lendo o código no aparelho.</span>
          </p>
        ) : null}
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <Button variant={desconectado ? 'primary' : 'secondary'} icon="atualizar" onClick={() => setQrAberto(true)}>
            Reconectar
          </Button>
          <span role="status">
            {pareado ? (
              <Badge tone="good" icon="check">
                Simulado: conectado agora
              </Badge>
            ) : null}
          </span>
        </div>
        <Modal
          open={qrAberto}
          onClose={fecharQr}
          title="Reconectar o WhatsApp"
          description={`Instância ${whatsapp.instanceName}. No celular da empresa: WhatsApp → Dispositivos conectados → Conectar dispositivo.`}
          footer={
            <>
              <Button variant="ghost" onClick={fecharQr}>
                Cancelar
              </Button>
              <Button variant="primary" icon="check" onClick={simularLeitura}>
                Simular leitura do código
              </Button>
            </>
          }
        >
          <div className="flex flex-col items-center gap-3 text-center">
            <QrCodeFalso seed={`${company.id}:${whatsapp.instanceName}`} />
            <p className="max-w-sm text-sm text-ink-muted">
              Este código é <strong className="text-ink">simulado</strong> e não pode ser escaneado. Na versão real, a Evolution API gera um código novo enquanto a leitura não
              acontece.
            </p>
          </div>
        </Modal>
      </SectionCard>

      <SectionCard
        id="voz"
        title="Voz (Vapi)"
        icon="telefone"
        description="Assistente que faz a ligação do 1º tier. O roteiro é editado na tela do funil."
        aside={
          <Badge tone={base.voice.state === 'ready' ? 'good' : 'warning'} dot>
            {base.voice.state === 'ready' ? 'Pronto' : 'Aguardando número'}
          </Badge>
        }
      >
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Nome do assistente" required htmlFor={ID_ASSISTENTE} error={tentouSalvar ? erroAssistente : null} hint="Como a IA se apresenta: “Aqui é a assistente da {empresa}”.">
            {({ id, describedBy, invalid }) => (
              <Input
                id={id}
                aria-describedby={describedBy}
                aria-invalid={invalid}
                value={draft.voice.assistantName}
                onChange={(event) => rascunho.setDraft((d) => ({ ...d, voice: { ...d.voice, assistantName: event.target.value } }))}
              />
            )}
          </Field>
          <Field label="Voz" htmlFor={ID_VOZ} hint="Timbre e ritmo da ligação. Todas em português do Brasil.">
            {({ id, describedBy }) => (
              <Select
                id={id}
                aria-describedby={describedBy}
                value={draft.voice.voice}
                onChange={(event) => {
                  const valor = event.target.value;
                  if (ehVoz(valor)) {
                    rascunho.setDraft((d) => ({ ...d, voice: { ...d.voice, voice: valor } }));
                  }
                }}
              >
                {VOICES.map((voz) => (
                  <option key={voz.value} value={voz.value}>
                    {voz.label}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <dl className="sm:col-span-2">
            <Dado rotulo="Número de origem" valor={maskPhoneDisplay(base.voice.originPhoneE164)} title="Número que aparece para o lead na ligação." />
            <p className="mt-1 text-xs text-ink-subtle">Número que aparece para o lead na ligação. A troca é feita pela administração.</p>
          </dl>
        </div>
        <div className="mt-4">
          <Footnote>Cada lead recebe no máximo 2 ligações (RF08), só dentro do horário permitido da aba Régua — e nunca antes de 30 minutos sem pagamento.</Footnote>
        </div>
      </SectionCard>

      <SectionCard
        id="email"
        title="E-mail"
        icon="email"
        description="Reserva do WhatsApp em cada toque. Entra na régua quando o provedor de envio for escolhido."
        aside={
          <Badge tone="warning" dot>
            Aguardando decisão
          </Badge>
        }
      >
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Provedor de envio" hint="Decisão pendente do dono do produto: Brevo ou Resend. Não depende da empresa.">
            {({ id, describedBy }) => (
              <Select id={id} aria-describedby={describedBy} value="a_definir" disabled onChange={() => undefined}>
                <option value="a_definir">A definir: Brevo ou Resend</option>
              </Select>
            )}
          </Field>
          <Field label="Domínio de envio" htmlFor={ID_DOMINIO} error={tentouSalvar ? erroDominio : null} hint="Domínio que assina os e-mails (SPF/DKIM). Ex.: envio.suaempresa.com.br">
            {({ id, describedBy, invalid }) => (
              <Input
                id={id}
                aria-describedby={describedBy}
                aria-invalid={invalid}
                inputMode="url"
                autoCapitalize="none"
                value={draft.email.sendingDomain}
                onChange={(event) => rascunho.setDraft((d) => ({ ...d, email: { ...d.email, sendingDomain: event.target.value } }))}
              />
            )}
          </Field>
        </div>
        <div className="mt-4">
          <Footnote>Hoje o motor fala por ligação e WhatsApp. Esta seção só guarda o domínio para a configuração ser imediata quando o provedor for definido.</Footnote>
        </div>
      </SectionCard>

      <Panel className="px-5 py-4">
        <BarraSalvar
          bordered={false}
          dirty={rascunho.dirty}
          saved={rascunho.saved}
          onSave={salvar}
          onDiscard={descartar}
          bloqueado={tentouSalvar && temErro}
          motivoBloqueio="Corrija os campos destacados."
          erros={erros}
        />
      </Panel>
    </div>
  );
}
